import Amount from "@components/amount";
import { productImageUrl } from "@provider/image-url";
import CustomImage from "@components/custom-image";
import AnimatedLink from "@components/animated-link";
import { roundness } from "@provider/dripsy";
import { moneyFormatter } from "@utils";
import { Text, useSx, View } from "dripsy";
import * as React from "react";

import routes from "../../NavigatorRoutes";
import type { ProductType } from "@type/global";
import Price from "../product/price";

type SimpleProductIngredient = {
  pid: number;
  quantity: number;
  name: string;
  recipe_id: number;
};

export const ITEM_HEIGHT_ARRAY = [90, 100, 130];

function RecipeProductItem({
  product,
  ingredient,
  onChange,
  labelPrefix = "Voor",
  labelPlacement = "above",
}: {
  product: ProductType | undefined;
  ingredient: SimpleProductIngredient;
  onChange: (value: number) => void;
  labelPrefix?: string;
  labelPlacement?: "above" | "inside";
}) {
  const sx = useSx();
  const itemLabel = ingredient.name.trim();
  const label = labelPrefix && itemLabel ? `${labelPrefix} ${itemLabel}` : itemLabel;
  const showLabelAbove = labelPlacement === "above" && label;
  const showLabelInside = labelPlacement === "inside" && label;
  const itemHeight = showLabelInside ? [98, 108, 130] : ITEM_HEIGHT_ARRAY;
  const animate = React.useMemo(
    () =>
      ({ hovered, pressed }: any) => {
        return {
          backgroundColor: hovered || pressed ? "#EDEDED" : "#fff",
        };
      },
    []
  );

  if (!product) {
    return (
      <>
        {showLabelAbove ? <Text sx={{ fontWeight: "500", color: "$textGrey" }}>{label}</Text> : null}
        <View sx={{ width: "100%", height: 40, display: "flex", justifyContent: "center", marginTop: -5 }}>
          <Text sx={{ fontSize: 16, fontWeight: "500", color: "$text" }}>Product is momenteel niet beschikbaar</Text>
        </View>
      </>
    );
  }

  const imagePadding = 10;
  const imageSize = [65, 85, 105];
  const imageSizeInner = imageSize.map((is) => is - imagePadding);
  const isStampProduct = product ? product.zegels > 0 : 0;
  return (
    <>
      {showLabelAbove ? <Text sx={{ fontWeight: "500", color: "$textGrey" }}>{label}</Text> : null}
      <View>
        <AnimatedLink
          to={routes.ProductDetailScreen}
          params={{
            id: `${product.id}`,
          }}
          animate={animate}
          style={sx({
            height: itemHeight,
            p: ["$2", "$3", "$4"],
            alignItems: "center",
            flexDirection: "row",
            borderRadius: roundness,
          })}
        >
          <View
            sx={{
              width: imageSize,
              height: imageSize,
              backgroundColor: "#fff",
              borderRadius: roundness,
            }}
          >
            <CustomImage
              source={{ uri: productImageUrl(product.pid, product.image_version) }}
              sx={{
                height: imageSizeInner,
                width: imageSizeInner,
                borderRadius: roundness,
                margin: imagePadding / 2,
              }}
              alt={product.pnm}
            />
          </View>

          <View
            sx={{
              flex: 1,
              px: ["$2", "$3"],
              pr: ["$15"],
              mr: ["$4", "$5"],
            }}
          >
            {showLabelInside ? (
              <Text
                numberOfLines={1}
                sx={{
                  color: "$textGrey",
                  fontSize: [12, 13],
                  fontWeight: "500",
                  marginBottom: "$1",
                }}
              >
                {label}
              </Text>
            ) : null}
            <Text sx={{ fontSize: [15, 16, 18], fontWeight: "700" }}>{product.pnm}</Text>
            {product.omschr1 && product.actieprijs <= 0 ? (
              <Text sx={{ color: "$textGrey" }} numberOfLines={1}>
                {product.omschr1}
              </Text>
            ) : null}
            <View sx={{ flexDirection: "row", alignItems: "flex-end", marginTop: "$1" }}>
              {isStampProduct ? (
                <>
                  <Text sx={{ fontWeight: "600" }}>{product.zegels}</Text>
                  <Text sx={{ color: "$textSecondary" }}> zegels en </Text>
                  <Text sx={{ fontWeight: "600" }}>{moneyFormatter.format(product.ppeg)}</Text>
                </>
              ) : (
                <Price actie={product.actie} actieprijs={product.actieprijs} ppeg={product.ppeg} />
              )}
              <View sx={{ marginLeft: isStampProduct ? "$1" : "$2", marginBottom: 0 }}>
                <Text
                  sx={{
                    fontSize: [11, 12, 14],
                    fontWeight: "400",
                    color: "$textGrey",
                  }}
                >
                  {product.pertext}
                </Text>
              </View>
            </View>
          </View>
        </AnimatedLink>
        <View
          sx={{
            alignItems: "flex-end",
            position: "absolute",
            right: [12, 24],
            top: 0,
            bottom: 0,
            justifyContent: "center",
          }}
        >
          <Amount shouldStretch={false} value={ingredient.quantity} onChange={onChange} />
        </View>
      </View>
    </>
  );
}

export default RecipeProductItem;
